import type { ExtraFieldsValues } from '@shared/model'
import type { TaskPriority, TasksKanbanTask } from '@entities/task'
import type { TasksKanbanStage } from '@brighterly/lib-core-types/tasks-kanban/enums'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

export interface KanbanTaskCreateSubmitPayload {
  task_type: string
  status: TasksKanbanStage
  priority: TaskPriority | null
  deadline: string | null
  assignee_id: number | null
  customer_id: number | null
  related_to_id: number | null
  source_task_id: number | null
  extra_fields: ExtraFieldsValues
}

interface KanbanTaskCreateSubmitApiOptions {
  task: KanbanTaskCreateSubmitPayload
  comment: string | null
  onStart?: () => void
  onSuccess?: (task: TasksKanbanTask) => void
  onError?: (err: unknown) => void
  onFinish?: () => void
}

export const useKanbanTaskCreateSubmitApi = async (
  options: KanbanTaskCreateSubmitApiOptions,
): Promise<TasksKanbanTask | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.kanban-tasks')

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    const { data } = await $axios.post(url!, {
      task: options.task,
      comment: options.comment,
    })
    const task: TasksKanbanTask = data.data

    commonStore.setLoading(false)
    options.onSuccess?.(task)

    return task
  } catch (err: unknown) {
    console.error(err)
    toast.error(getAxiosErrorMessage(err))
    commonStore.setLoading(false)
    options.onError?.(err)

    return null
  } finally {
    options.onFinish?.()
  }
}
